import { useCallback, useState } from 'react';
import type { UserFeedbackRating } from 'generated/graphql';
import { SITE_FEEDBACK_API_ROUTE } from '~/routes/remix-api.site-feedback';

export interface SiteFeedbackInput {
    rating: UserFeedbackRating;
    /** Already trimmed by the form; an empty string stores no comment. */
    comment: string;
}

/**
 * Post one piece of feedback about the page the reader is on.
 *
 * Posted with `fetch` rather than a route fetcher so the caller can await the outcome and be told
 * the truth about it. The whole point of the button is that what someone typed reaches the team;
 * a form that says "thanks" on a failed write is worse than one that never shipped, because the
 * feedback is gone and nobody knows to ask for it again.
 *
 * The URL is read here rather than taken from the caller, so every call site records the same
 * thing. Reading it at submit time is safe because the form is a modal: the reader cannot browse
 * on while it is up, so the page under it is still the page they are describing.
 *
 * @throws Error When the request fails, or the API answers with anything but a stored row
 */
export const useSiteFeedback = () => {
    const [submitting, setSubmitting] = useState(false);

    const submit = useCallback(
        async ({ rating, comment }: SiteFeedbackInput) => {
            setSubmitting(true);
            try {
                const body = new FormData();
                body.append('pageUrl', window.location.href);
                body.append('rating', rating);
                if (comment) body.append('comment', comment);

                const response = await fetch(SITE_FEEDBACK_API_ROUTE, {
                    method: 'POST',
                    body,
                });
                if (!response.ok) {
                    throw new Error(
                        `Feedback request failed (${response.status})`,
                    );
                }

                // The mutation answers with a union: a stored UserFeedback, or one of the error
                // members, which arrive as a 200 and would otherwise read as success.
                const result = (await response.json()) as {
                    userFeedbackUpsert?: {
                        __typename?: string;
                        Message?: string;
                    };
                };
                const payload = result.userFeedbackUpsert;
                if (payload?.__typename !== 'UserFeedback') {
                    throw new Error(
                        payload?.Message ?? 'Feedback was not stored',
                    );
                }
            } finally {
                setSubmitting(false);
            }
        },
        [],
    );

    return { submit, submitting };
};
